import type {
  TrainingAnswer,
  TrainingAnswers,
  TrainingModuleSchema,
  TrainingPlayerPage,
  TrainingSectionGrade,
  TrainingServeOptions,
} from '@hcc/types';
import {
  gradeTrainingAttempt,
  gradeTrainingQuestion,
  projectTrainingPlayerPages,
  trainingAttemptPassed,
  trainingQuestions,
} from '@hcc/types';

/**
 * Builder-preview binding of the shared player projection + grader (see
 * @hcc/types trainingPlayer). The admin authored the answers, so nothing is
 * secret here; the point is letting them try the module (including scoring)
 * without publishing. Display order is rotated rather than randomly shuffled
 * so the preview is stable across re-renders, and matching label ids are a
 * plain prefix instead of the server's seeded hash. The question-bank draw
 * uses a seeded shuffle keyed by a draw seed so "Reset" can show a new
 * subset, exactly as a fresh attempt would on the server.
 */

function rotated<T>(items: readonly T[]): T[] {
  if (items.length < 2) return [...items];
  return [...items.slice(1), items[0]!];
}

function previewLabelId(pairId: string): string {
  return `preview-label-${pairId}`;
}

/** mulberry32 — same tiny PRNG the server uses, for the question draw. */
function seededShuffle<T>(items: readonly T[], seed: number): T[] {
  const out = items.slice();
  let a = seed >>> 0;
  const rand = () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

export interface PreviewServeConfig {
  serveQuestionCount: number | null;
  shuffleQuestions: boolean;
}

/** Serve options for the preview, redrawn whenever drawSeed changes. */
export function previewServe(config: PreviewServeConfig, drawSeed: number): TrainingServeOptions {
  return {
    serveQuestionCount: config.serveQuestionCount,
    shuffleQuestions: config.shuffleQuestions,
    shuffle: (items) => seededShuffle(items, drawSeed),
  };
}

export function toPreviewPages(
  schema: TrainingModuleSchema,
  serve?: TrainingServeOptions,
): TrainingPlayerPage[] {
  return projectTrainingPlayerPages(
    schema,
    {
      matchLabelId: previewLabelId,
      shuffleOrdering: (items) => rotated(items),
      shuffleLabels: (labels) => rotated(labels),
    },
    serve,
  );
}

export function gradePreview(
  schema: TrainingModuleSchema,
  answers: TrainingAnswers,
  passMark: number,
  serve?: TrainingServeOptions,
): { score: number; passed: boolean; sections: TrainingSectionGrade[] } {
  const grade = gradeTrainingAttempt(schema, answers, previewLabelId, serve);
  return {
    score: grade.score,
    passed: trainingAttemptPassed(grade, passMark),
    sections: grade.sections,
  };
}

/** Grade one question locally (try_again preview). False if unknown/unanswered. */
export function gradePreviewQuestion(
  schema: TrainingModuleSchema,
  questionId: string,
  answer: TrainingAnswer | undefined,
): boolean {
  const q = trainingQuestions(schema).find((x) => x.id === questionId);
  return q ? gradeTrainingQuestion(q, answer, previewLabelId) : false;
}
